import { useIdleTimer, workerTimers } from "react-idle-timer";
import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../utils/AuthContext";
import "./UserIdle.css";


export const MAX_LOGIN_TIME = "hash_log";

const config = {
  "timeOutInMin": 30,
  "promptBeforeIdleInMin": 2,
  "maxLoginTimeInMin": 720,
  "maxLoginTimeBeforeIdleInMin": 30
}


export function formatTime(seconds) {
  // Ensure the input is a valid number
  if (isNaN(seconds) || seconds < 0) {
    return "Invalid input";
  }

  // Calculate hours, minutes, and seconds
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  // Format each unit to be two digits
  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(secs).padStart(2, "0");

  // If hours are zero, return mm:ss format
  if (hours === 0) {
    return `${formattedMinutes}:${formattedSeconds}`;
  }

  // Otherwise, return hh:mm:ss format
  const formattedHours = String(hours).padStart(2, "0");
  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

export function minutesToMilliseconds(minutes) {
  // Ensure the input is a valid number
  if (isNaN(minutes) || minutes < 0) {
    return "Invalid input";
  }

  // Convert minutes to milliseconds
  const milliseconds = +minutes * 60 * 1000;
  return milliseconds;
}




const key = "abc";
const textToChars = (text) => text.split("").map((c) => c.charCodeAt(0));
const applySaltToChar = (code) =>
  textToChars(key).reduce((a, b) => a ^ b, code);

export const simpleEncode = (text) => {
  const byteHex = (n) => ("0" + Number(n).toString(16)).slice(-2);
  return (
    "a" +
    text.split("").map(textToChars).map(applySaltToChar).map(byteHex).join("") +
    "b"
  );
};

export const simpleDecode = (encoded) => {
  if (
    encoded &&
    encoded.length > 0 &&
    encoded[0] === "a" &&
    encoded[encoded.length - 1] === "b"
  ) {
    const text = encoded.substring(1, encoded.length - 1);
    return text
      .match(/.{1,2}/g)
      .map((hex) => Number.parseInt(hex, 16))
      .map(applySaltToChar)
      .map((charCode) => String.fromCharCode(charCode))
      .join("");
  }
  console.error("unable to decode");
  return "";
};


const setUserSessionTime = (time = 24 * 60 * 60 * 1000) => {
  localStorage.setItem(MAX_LOGIN_TIME, time);
};

const getUserSessionTime = () => {
  return localStorage.getItem(MAX_LOGIN_TIME);
};

export function setLogoutSession() {
  // Get current time
  const currentTime = new Date().getTime();
  // Set expiry time for 24 hours from now
  const expiryTime =
    currentTime + minutesToMilliseconds(config.maxLoginTimeInMin);
  setUserSessionTime(simpleEncode(String(expiryTime)));
}




let rr;

function checkLogoutTime(callback) {
  // Retrieve encoded expiry time from cookies
  let userTime = getUserSessionTime();

  clearTimeout(rr);

  if (userTime && simpleDecode(userTime)) {
    userTime = +simpleDecode(userTime);

    // Get the current time
    const currentTime = new Date().getTime();

    // Check if the current time is past the expiry time
    if (currentTime >= userTime) {
      callback && callback({ message: "zero min left!", status: false });
      return;
    }
    // Check if the remaining time is less than 15 minutes (900,000 ms)
    const remainingTime = userTime - currentTime;

    if (
      remainingTime < minutesToMilliseconds(config.maxLoginTimeBeforeIdleInMin)
    ) {
      setLogoutSession();
      callback &&
        callback({
          message: `less than ${millisToMinutes(remainingTime)} min`,
          status: true,
        });
      return;
    }

    callback &&
      callback({
        message: "access allowed",
        status: true,
        left: millisToMinutes(remainingTime),
      });
  } else {
    rr = setTimeout(() => {
      callback && callback({ message: "Session not found", status: false });
    }, 5000);
  }
}

function millisToMinutes(millis) {
  return millis / (1000 * 60);
}

function UserIdle({ children, userID }) {
  // Set timeout values
  const timeout = minutesToMilliseconds(config.timeOutInMin);
  const promptBeforeIdle = minutesToMilliseconds(config.promptBeforeIdleInMin);

  // console.log({ timeout, promptBeforeIdle });

  // const { onLogoutFromContext } = useAppContext();

  // Modal open state
  const [open, setOpen] = useState(false);

  // Time before idle
  const [remaining, setRemaining] = useState(0);

  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const onLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Idle logout failed:", error);
    } finally {
      // Ensure the local session is cleared and the user is booted to the
      // login/landing page even if the logout API call itself failed.
      localStorage.clear();
      navigate("/");
    }
  };

  useEffect(() => {
    checkLogoutTime(({ status, message, left }) => {
      // console.log({ status, message, left, userID });

      if (!status) {
        onLogout();
      }
    });
  }, []);

  const onPrompt = useCallback(() => {
    // onPrompt will be called `promptBeforeIdle` milliseconds before `timeout`.
    // In this case 29 minutes and 30 seconds or 30 seconds before idle.
    // Here you can open your prompt.
    // All events are disabled while the prompt is active.
    // If the user wishes to stay active, call the `activate()` method.
    // You can get the remaining prompt time with the `getRemainingTime()` method,
    setOpen(true);
    setRemaining(promptBeforeIdle);
  }, [userID]);

  const onIdle = useCallback(() => {
    // onIdle will be called after the timeout is reached.
    // In this case 30 minutes. Here you can close your prompt and
    // perform whatever idle action you want such as logging out your user.
    // Events will be rebound as long as `stopOnMount` is not set.
    setOpen(false);
    setRemaining(0);

    // console.log("onIdle");
    setTimeout(() => {
      onLogout();
    }, 1);
  }, [userID]);

  const onActive = () => {
    // onActive will only be called if `activate()` is called while `isPrompted()`
    // is true. Here you will also want to close your modal and perform
    // any active actions.
    setOpen(false);
    setRemaining(0);
    console.log("onActive");
  };

  const { getRemainingTime, isPrompted, activate } = useIdleTimer({
    timeout,
    promptBeforeIdle,
    onPrompt,
    onIdle,
    onActive,
    crossTab: true,
    timers: workerTimers,
    events: [
      "mousemove",
      "keydown",
      "wheel",
      "DOMMouseScroll",
      "mousewheel",
      "mousedown",
      "touchstart",
      "touchmove",
      "MSPointerDown",
      "MSPointerMove",
      "visibilitychange",
      "timeupdate",
    ],
  });

  const handleStillHere = () => {
    setOpen(false);
    activate();
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPrompted()) {
        setRemaining(Math.ceil(getRemainingTime() / 1000));
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [getRemainingTime, isPrompted]);

  return (
    <>
      {children}
      {open ? (
        <div className="ui-session-overlay">
          <div
            className="ui-session-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="ui-session-title"
            aria-describedby="ui-session-message"
          >
            <div className="ui-session-modal__icon">
              <i className="fas fa-clock" aria-hidden="true" />
            </div>
            <h2 id="ui-session-title" className="ui-session-modal__title">
              Session About to Expire
            </h2>
            <p id="ui-session-message" className="ui-session-modal__message">
              For your security, you’ll be logged out automatically due to
              inactivity.
            </p>
            <div className="ui-session-modal__timer">
              {formatTime(remaining)}
            </div>
            <div className="ui-session-modal__actions">
              <button
                type="button"
                className="ui-session-modal__btn ui-session-modal__btn--secondary"
                onClick={onLogout}
              >
                Logout Now
              </button>
              <button
                type="button"
                className="ui-session-modal__btn ui-session-modal__btn--primary"
                onClick={handleStillHere}
              >
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default UserIdle;
